import { useCallback, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Text, Tooltip, TooltipPlacement } from '../../ui-components';
import { GetIcon } from '../icons';

interface BackorderThresholdMessageProps {
    selectedQuantityInStock: number;
    selectedQuantityInBackorder: number;
    backorderInStockDate: number;
}

export const BackorderThresholdMessage: FC<BackorderThresholdMessageProps> = ({
    selectedQuantityInStock,
    selectedQuantityInBackorder,
    backorderInStockDate,
}) => {
    const BoldText = useCallback(
        (chunks: ReactNode[]) => (
            <Text size="small" style="bold" as="span">
                {chunks}
            </Text>
        ),
        [],
    );

    const formattedBackInStockDate = useMemo(
        () =>
            new Intl.DateTimeFormat('default', {
                month: 'short',
                weekday: 'short',
                day: 'numeric',
                year: 'numeric',
            }).format(backorderInStockDate),
        [backorderInStockDate],
    );

    return (
        <div
            className="flex items-center gap-1"
            data-testid="quantity-message-in-backorder-threshold"
        >
            <Text size="small" color="slt-red.1000">
                <FormattedMessage
                    id="quantity.message.items.available.backorder"
                    defaultMessage="{selectedQuantityInBackorder, plural, one {# item ships} other {# items ship}} on {backorderInStockDate}."
                    values={{
                        selectedQuantityInBackorder,
                        backorderInStockDate: new Intl.DateTimeFormat('default').format(
                            backorderInStockDate,
                        ),
                    }}
                />
            </Text>
            <Tooltip
                label={
                    <Text size="small">
                        <FormattedMessage
                            id="quantity.message.backordered.instockdate"
                            defaultMessage="{selectedQuantityInStock} item(s) in-stock, and {selectedQuantityInBackorder} item(s) on backorder. The expected stock date is <bold>{backorderInStockDate}</bold>."
                            values={{
                                selectedQuantityInStock,
                                selectedQuantityInBackorder,
                                backorderInStockDate: formattedBackInStockDate,
                                bold: BoldText,
                            }}
                        />
                    </Text>
                }
                placement={TooltipPlacement.top}
            >
                <div className="flex" data-testid="quantity-message-quantity-in-backorder-tooltip">
                    <GetIcon name="info" size="sm" />
                </div>
            </Tooltip>
        </div>
    );
};
